import "./colors.css";
import "./style.css";
import "./firebase.js";
import {
  EMOJIS_FLASHCARD,
  EMOJIS_FULL_EXAM,
  EMOJIS_PRACTICE,
  EMOJIS_STATS,
  EMOJIS_TEST_BASE,
  EMOJIS_TIMED,
  FULL_TEST_COUNT,
  FULL_TEST_TIME,
  LABEL_MODE_FLASHCARD,
  LABEL_MODE_FULL_MOCK_EXAM,
  LABEL_MODE_MINI_TEST,
  LABEL_MODE_PRACTICE,
  LABEL_MODE_STATS,
  MINI_TEST_COUNT,
  MINI_TEST_TIME,
} from "./constants.js";
import explanations from "./data/explanations.json";
import questionsTest from "./data/questions-test.json";
import questions from "./data/questions.json";
import { triggerGlitter } from "./glitter.js";
import { loadNextQuestion, startFlashcardMode, startPracticeMode, startTestMode } from "./quiz.js";
import { els, state } from "./state.js";
import { showStats } from "./stats-view.js";
import { loadStats, loadTheme, saveTheme } from "./storage.js";

import { calculateMasteryLevel } from "./utils.js";

const init = async () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    state.allQuestions = urlParams.get("data") === "test" ? questionsTest : questions;
    state.explanations = explanations;

    loadStats();
    state.theme = loadTheme();
    applyTheme();

    els.themeToggle.onclick = toggleTheme;

    els.loading.style.display = "none";

    window.state = state;

    // Add listeners after initial setup is complete
    window.addEventListener("popstate", handleRouting);
    window.addEventListener("navigate", (e) => navigateTo(e.detail));
    window.addEventListener("hideAllViews", hideAllViews);

    els.btnPractice.onclick = () => navigateTo("/practice");
    els.btnPracticeReview.onclick = () => navigateTo("/practice?review=1");
    els.btnFlashcard.onclick = () => navigateTo("/flashcard");
    els.btnFlashcardReview.onclick = () => navigateTo("/flashcard?review=1");
    els.btnTestMini.onclick = () => navigateTo("/test-mini");
    els.btnTestFull.onclick = () => navigateTo("/test-full");
    els.btnStats.onclick = () => navigateTo("/stats");
    els.btnHome.onclick = () => navigateTo("/");
    els.nextBtn.onclick = () => loadNextQuestion(true);
    els.btnQuit.onclick = () => navigateTo("/");
    els.btnStatsBack.onclick = () => navigateTo("/");
    els.btnResultsBack.onclick = () => navigateTo("/");

    // Unregister any existing service workers (cleanup)
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    }

    handleRouting();
  } catch (e) {
    els.loading.textContent = "Error: " + e.message + ". Ensure questions.json is present.";
  }
};

const navigateTo = (path) => {
  window.history.pushState({}, "", path);
  handleRouting();
};

const hideAllViews = () => {
  els.menu.style.display = "none";
  els.quiz.style.display = "none";
  els.results.style.display = "none";
  els.stats.style.display = "none";
  els.timer.style.display = "none";
};

const renderMenu = () => {
  // Check if any active questions need review
  const validIds = new Set(state.allQuestions.map((q) => q.id));
  const hasNeedsReview = Object.entries(state.userProgress.history).some(
    ([id, h]) => validIds.has(id) && calculateMasteryLevel(h) === 1,
  );

  els.btnPractice.innerHTML = `<span class="option-label">✨</span> ${LABEL_MODE_PRACTICE}`;
  els.btnFlashcard.innerHTML = `<span class="option-label">🃏</span> ${LABEL_MODE_FLASHCARD}`;

  if (hasNeedsReview) {
    els.btnPracticeReview.style.display = "flex";
    els.btnFlashcardReview.style.display = "flex";
    els.btnPractice.classList.remove("full-width");
    els.btnFlashcard.classList.remove("full-width");
  } else {
    els.btnPracticeReview.style.display = "none";
    els.btnFlashcardReview.style.display = "none";
    els.btnPractice.classList.add("full-width");
    els.btnFlashcard.classList.add("full-width");
  }

  els.btnTestMini.innerHTML =
    `<span class="option-label">⏱️</span> ${LABEL_MODE_MINI_TEST} <small>${MINI_TEST_COUNT} Questions, ${MINI_TEST_TIME} Minutes</small>`;
  els.btnTestFull.innerHTML =
    `<span class="option-label">💪</span> ${LABEL_MODE_FULL_MOCK_EXAM} <small>${FULL_TEST_COUNT} Questions, ${FULL_TEST_TIME} Minutes</small>`;
  els.btnStats.innerHTML = `<span class="option-label">📊</span> ${LABEL_MODE_STATS}`;
};

const handleRouting = () => {
  const path = window.location.pathname.replace(/^\//, "");
  const urlParams = new URLSearchParams(window.location.search);
  const qId = urlParams.get("q");
  const reviewOnly = urlParams.get("review") === "1";

  clearInterval(state.test.timerInterval);
  state.sessionStreak = 0;
  hideAllViews();

  if (qId && path !== "stats") {
    if (path === "flashcard") {
      triggerGlitter({ emojis: EMOJIS_FLASHCARD });
      startFlashcardMode(qId, reviewOnly);
    } else {
      triggerGlitter({ emojis: EMOJIS_PRACTICE });
      startPracticeMode(qId, null, reviewOnly);
    }
    return;
  }

  if (!path || path === "" || path === "index.html") {
    els.menu.style.display = "block";
    renderMenu();
    return;
  }

  switch (path) {
    case "practice":
      const topic = urlParams.get("topic");
      triggerGlitter({ emojis: EMOJIS_PRACTICE });
      startPracticeMode(null, topic, reviewOnly);
      break;
    case "flashcard":
      triggerGlitter({ emojis: EMOJIS_FLASHCARD });
      startFlashcardMode(null, reviewOnly);
      break;
    case "test-mini":
      triggerGlitter({ emojis: [...EMOJIS_TEST_BASE, ...EMOJIS_TIMED] });
      startTestMode(MINI_TEST_COUNT, MINI_TEST_TIME);
      break;
    case "test-full":
      triggerGlitter({ emojis: [...EMOJIS_TEST_BASE, ...EMOJIS_TIMED, ...EMOJIS_FULL_EXAM] });
      startTestMode(FULL_TEST_COUNT, FULL_TEST_TIME);
      break;
    case "stats":
      triggerGlitter({ emojis: EMOJIS_STATS });
      showStats();
      break;
    default:
      showMenu();
      break;
  }
};

const showMenu = () => {
  state.mode = null;
  els.menu.style.display = "block";
};

const applyTheme = () => {
  const theme = state.theme;
  document.body.classList.remove("light-mode", "dark-mode");

  if (theme === "light") {
    document.body.classList.add("light-mode");
    els.themeToggle.textContent = "☀️";
  } else if (theme === "dark") {
    document.body.classList.add("dark-mode");
    els.themeToggle.textContent = "🌙";
  } else {
    // system
    els.themeToggle.textContent = "🌓";
  }
};

const toggleTheme = () => {
  if (state.theme === "system") {
    state.theme = "light";
  } else if (state.theme === "light") {
    state.theme = "dark";
  } else {
    state.theme = "system";
  }
  saveTheme(state.theme);
  applyTheme();
};

init();
